/**
 * The one definition of a valid rec league social link.
 *
 * `parseSocialLink` turns whatever a person typed into a profile field (a
 * username, a bare domain, a pasted share link) into the canonical URL we
 * store, or says why it can't. The API enforces it on every write, the web
 * and mobile forms use it for inline errors, and the data-cleanup job uses it
 * to audit stored rows, so "valid" means the same thing everywhere.
 *
 * Pure and dependency-free (no `URL`, no imports): input is arbitrary user
 * text, so nothing here may throw. This file is byte-identical in
 * `web/util/socialLinkParser.ts` and `mobile/lib/util/socialLinkParser.ts`;
 * edit both together.
 */

export type SocialLinkField =
  | "instagram_url"
  | "tiktok_url"
  | "youtube_url"
  | "x_url"
  | "threads_url"
  | "spotify_url"
  | "letterboxd_url"
  | "goodreads_url"
  | "substack_url"
  | "website_url";

export type SocialLinkRejectReason =
  /** An email address, alone or wrapped in a profile URL. */
  | "email"
  /** Whitespace inside the value: a name, a sentence, or several handles. */
  | "spaces"
  /** Characters or a length the platform doesn't allow in a username. */
  | "invalid_handle"
  /** A link to some other site. */
  | "wrong_site"
  /** The right site, but a post/video/search page instead of a profile. */
  | "not_profile"
  /** Not recognizable as a web address. */
  | "invalid_url"
  | "too_long";

export type ParsedSocialLink =
  | { ok: true; url: string }
  | { ok: false; reason: SocialLinkRejectReason };

/** The `*_url` columns are varchar(255). */
export const MAX_SOCIAL_LINK_LENGTH = 255;

const ok = (url: string): ParsedSocialLink =>
  url.length > MAX_SOCIAL_LINK_LENGTH
    ? { ok: false, reason: "too_long" }
    : { ok: true, url };

const reject = (reason: SocialLinkRejectReason): ParsedSocialLink => ({
  ok: false,
  reason,
});

// ---------------------------------------------------------------------------
// URL splitting
// ---------------------------------------------------------------------------

interface SplitUrl {
  scheme: "http" | "https";
  /** Lowercased, without port or trailing dot. */
  host: string;
  port: string;
  hasUserInfo: boolean;
  /** Path segments, empty ones dropped. */
  segments: string[];
  /** Everything from the first "/" on, exactly as typed ("" when absent). */
  pathAndAfter: string;
}

// "https://", and the ways people mangle it: "HTTP://", "https//", "https:/".
const SCHEME_PREFIX = /^(https?)(?::\/{0,2}|\/\/)/i;

/**
 * Peels every leading scheme off the input. Stored data has doubled schemes
 * ("https://HTTP://site.com") from when a scheme-blind normalizer wrapped an
 * uppercase one; the innermost scheme is the one the person typed.
 */
function stripSchemes(value: string): {
  rest: string;
  scheme: "http" | "https" | null;
} {
  let rest = value;
  let scheme: "http" | "https" | null = null;
  for (
    let match = SCHEME_PREFIX.exec(rest);
    match;
    match = SCHEME_PREFIX.exec(rest)
  ) {
    scheme = match[1].toLowerCase() as "http" | "https";
    rest = rest.slice(match[0].length);
  }
  return { rest, scheme };
}

const HOSTNAME_PATTERN =
  /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:[a-z]{2,}|xn--[a-z0-9-]+)$/;

/** Splits scheme-less text ("host/path?query#hash"); null if no real host. */
function splitUrl(rest: string, scheme: "http" | "https"): SplitUrl | null {
  const authorityEnd = rest.search(/[/?#]/);
  const authority = authorityEnd === -1 ? rest : rest.slice(0, authorityEnd);
  const afterAuthority = authorityEnd === -1 ? "" : rest.slice(authorityEnd);

  const atIndex = authority.lastIndexOf("@");
  const hostPort = authority.slice(atIndex + 1);
  const portMatch = /:(\d{1,5})$/.exec(hostPort);
  const host = (portMatch ? hostPort.slice(0, portMatch.index) : hostPort)
    .toLowerCase()
    .replace(/\.$/, "");
  if (!HOSTNAME_PATTERN.test(host)) return null;

  const path = afterAuthority.split(/[?#]/, 1)[0];
  return {
    scheme,
    host,
    port: portMatch ? portMatch[1] : "",
    hasUserInfo: atIndex !== -1,
    segments: path.split("/").filter(Boolean),
    // A query or hash straight after the host still needs its "/" to be a URL
    pathAndAfter:
      afterAuthority && !afterAuthority.startsWith("/")
        ? `/${afterAuthority}`
        : afterAuthority,
  };
}

/** The host without the prefixes platforms serve interchangeably. */
const bareHost = (host: string) => host.replace(/^(?:www|m|mobile)\./, "");

const EMAIL_PATTERN = /^[^\s@/]+@[^\s@/]+\.[a-z]{2,}$/i;

// ---------------------------------------------------------------------------
// Platforms
// ---------------------------------------------------------------------------

type PlatformField = Exclude<SocialLinkField, "website_url" | "substack_url">;

interface PlatformSpec {
  /** Accepted bare hosts → the origin we store for each. */
  origins: Record<string, string>;
  /** Hosts that belong to the platform but never point at a profile. */
  shareHosts?: readonly string[];
  handlePattern: RegExp;
  /** First path segments that are site sections, not usernames. */
  reserved: readonly string[];
  /** Profile path for a username. */
  handlePath: (handle: string) => string;
  /**
   * Profile paths that aren't `/<username>`: the first `depth` segments are
   * kept when the first is one of `kinds` (YouTube `/channel/<id>`, Goodreads
   * `/user/show/<id>`).
   */
  structured?: { kinds: readonly string[]; depth: 2 | 3 };
}

const INSTAGRAM_HANDLE = /^[A-Za-z0-9._]{1,30}$/;

const PLATFORMS: Record<PlatformField, PlatformSpec> = {
  instagram_url: {
    origins: { "instagram.com": "https://instagram.com" },
    handlePattern: INSTAGRAM_HANDLE,
    reserved: ["p", "reel", "reels", "stories", "explore", "tv", "accounts", "direct", "share"],
    handlePath: (handle) => `/${handle}`,
  },
  threads_url: {
    origins: {
      "threads.net": "https://threads.net",
      "threads.com": "https://threads.com",
    },
    handlePattern: INSTAGRAM_HANDLE,
    reserved: ["t", "post", "search", "login"],
    handlePath: (handle) => `/@${handle}`,
  },
  tiktok_url: {
    origins: { "tiktok.com": "https://tiktok.com" },
    shareHosts: ["vm.tiktok.com", "vt.tiktok.com"],
    handlePattern: /^[A-Za-z0-9._]{2,24}$/,
    reserved: ["t", "discover", "tag", "music", "video", "foryou", "explore", "live", "search"],
    handlePath: (handle) => `/@${handle}`,
  },
  x_url: {
    origins: {
      "x.com": "https://x.com",
      "twitter.com": "https://twitter.com",
    },
    handlePattern: /^[A-Za-z0-9_]{1,15}$/,
    reserved: ["home", "i", "intent", "search", "explore", "share", "hashtag", "messages", "settings"],
    handlePath: (handle) => `/${handle}`,
  },
  youtube_url: {
    origins: { "youtube.com": "https://www.youtube.com" },
    shareHosts: ["youtu.be", "music.youtube.com"],
    handlePattern: /^[A-Za-z0-9._-]{3,30}$/,
    reserved: ["watch", "playlist", "shorts", "feed", "results", "embed", "live", "hashtag", "post", "clip"],
    // youtube.com/<handle> without the "@" is a 404
    handlePath: (handle) => `/@${handle}`,
    structured: { kinds: ["channel", "c", "user"], depth: 2 },
  },
  spotify_url: {
    origins: { "open.spotify.com": "https://open.spotify.com" },
    shareHosts: ["spotify.link", "spotify.com"],
    handlePattern: /^[A-Za-z0-9._-]{1,64}$/,
    reserved: ["track", "album", "episode", "search", "genre", "collection"],
    handlePath: (handle) => `/user/${handle}`,
    structured: { kinds: ["user", "artist", "show", "playlist"], depth: 2 },
  },
  letterboxd_url: {
    origins: {
      "letterboxd.com": "https://letterboxd.com",
      // Letterboxd's own share button hands out boxd.it short links
      "boxd.it": "https://boxd.it",
    },
    handlePattern: /^[A-Za-z0-9_]{1,30}$/,
    reserved: ["film", "films", "list", "lists", "members", "journal", "activity", "search", "tag"],
    handlePath: (handle) => `/${handle}`,
  },
  goodreads_url: {
    origins: { "goodreads.com": "https://www.goodreads.com" },
    handlePattern: /^[A-Za-z0-9._-]{1,50}$/,
    reserved: ["book", "review", "list", "search", "quotes", "genres", "group", "topic", "series"],
    handlePath: (handle) => `/${handle}`,
    structured: { kinds: ["user", "author"], depth: 3 },
  },
};

/** Every host that belongs to a social platform (for telling a pasted link from a handle). */
const SOCIAL_HOSTS: ReadonlySet<string> = new Set([
  ...Object.values(PLATFORMS).flatMap((spec) => [
    ...Object.keys(spec.origins),
    ...(spec.shareHosts ?? []),
  ]),
  "substack.com",
  "open.substack.com",
]);

const isSocialHost = (host: string) =>
  SOCIAL_HOSTS.has(bareHost(host)) || host.endsWith(".substack.com");

/**
 * A username segment → the handle, or the reason it isn't one. An "@" anywhere
 * but the front means someone typed an email address into a profile field.
 */
function parseHandle(
  segment: string,
  pattern: RegExp,
): { handle: string } | { reason: SocialLinkRejectReason } {
  const handle = segment.replace(/^@+/, "");
  if (EMAIL_PATTERN.test(handle)) return { reason: "email" };
  // "www.mysite.org" passes Instagram's charset, but nobody's handle starts "www."
  if (/^www\./i.test(handle)) return { reason: "wrong_site" };
  return pattern.test(handle) ? { handle } : { reason: "invalid_handle" };
}

function parsePlatformLink(
  spec: PlatformSpec,
  text: string,
  url: SplitUrl | null,
): ParsedSocialLink {
  if (!url) {
    const parsed = parseHandle(text, spec.handlePattern);
    if ("reason" in parsed) return reject(parsed.reason);
    const [origin] = Object.values(spec.origins);
    return ok(`${origin}${spec.handlePath(parsed.handle)}`);
  }

  const host = bareHost(url.host);
  if (spec.shareHosts?.includes(host)) return reject("not_profile");
  const origin = spec.origins[host];
  if (!origin) return reject("wrong_site");

  // A locale prefix (open.spotify.com/intl-de/user/...) is the same page
  const segments = /^intl-[a-z]{2}$/i.test(url.segments[0] ?? "")
    ? url.segments.slice(1)
    : url.segments;
  const [first] = segments;
  if (!first) return reject("not_profile");

  if (spec.structured?.kinds.includes(first.toLowerCase())) {
    const { depth } = spec.structured;
    return segments.length >= depth
      ? ok(`${origin}/${segments.slice(0, depth).join("/")}`)
      : reject("not_profile");
  }

  if (spec.reserved.includes(first.toLowerCase())) return reject("not_profile");

  // boxd.it short codes aren't usernames; keep the link as pasted
  if (host === "boxd.it") return ok(`${origin}/${first}`);

  // Anything past the username (/reels/, /tagged, ?igsh=...) isn't the
  // profile: tracking params in particular identify whoever shared the link.
  const parsed = parseHandle(first, spec.handlePattern);
  if ("reason" in parsed) return reject(parsed.reason);
  return ok(`${origin}${spec.handlePath(parsed.handle)}`);
}

const SUBSTACK_HANDLE = /^[A-Za-z0-9_-]{1,50}$/;
const SUBSTACK_RESERVED = ["home", "inbox", "search", "sign-in", "browse", "app"];

/**
 * A Substack is a profile (substack.com/@handle), a publication
 * (name.substack.com), or a custom domain, so unlike the other platforms it
 * can't be held to a host allowlist.
 */
function parseSubstackLink(text: string, url: SplitUrl | null): ParsedSocialLink {
  if (!url) {
    const parsed = parseHandle(text, SUBSTACK_HANDLE);
    if ("reason" in parsed) return reject(parsed.reason);
    return ok(`https://substack.com/@${parsed.handle}`);
  }

  // "name.substack" is a publication address missing its ".com"
  const host = bareHost(url.host).replace(/\.substack$/, ".substack.com");
  const [first, second] = url.segments;

  if (host === "substack.com") {
    if (!first || SUBSTACK_RESERVED.includes(first.toLowerCase())) {
      return reject("not_profile");
    }
    if (first.toLowerCase() === "profile") {
      return second
        ? ok(`https://substack.com/profile/${second}`)
        : reject("not_profile");
    }
    const parsed = parseHandle(first, SUBSTACK_HANDLE);
    if ("reason" in parsed) return reject(parsed.reason);
    return ok(`https://substack.com/@${parsed.handle}`);
  }

  if (host === "open.substack.com") {
    // The share-sheet link for a publication
    if (first?.toLowerCase() === "pub" && second && SUBSTACK_HANDLE.test(second)) {
      return ok(`https://${second.toLowerCase()}.substack.com`);
    }
    if (first?.startsWith("@")) {
      const parsed = parseHandle(first, SUBSTACK_HANDLE);
      if ("reason" in parsed) return reject(parsed.reason);
      return ok(`https://substack.com/@${parsed.handle}`);
    }
    if (first?.toLowerCase() === "users" && second) {
      return ok(`https://open.substack.com/users/${second}`);
    }
    return reject("not_profile");
  }

  if (host.endsWith(".substack.com")) {
    // Substack's certificate covers one label: www.name.substack.com fails TLS
    const publication = host.slice(0, -".substack.com".length);
    return publication.includes(".")
      ? reject("invalid_url")
      : ok(`https://${publication}.substack.com`);
  }

  return parseWebsiteLink(url);
}

function parseWebsiteLink(url: SplitUrl | null): ParsedSocialLink {
  if (!url) return reject("invalid_url");
  // "name@site.com" splits as userinfo + host
  if (url.hasUserInfo) return reject(url.segments.length ? "invalid_url" : "email");
  const port = url.port ? `:${url.port}` : "";
  const pathAndAfter = url.pathAndAfter === "/" ? "" : url.pathAndAfter;
  return ok(`${url.scheme}://${url.host}${port}${pathAndAfter}`);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parses one non-blank profile-link input for `field`. Blank input means
 * "clear the link" and is the caller's to handle.
 */
export function parseSocialLink(
  field: SocialLinkField,
  input: string,
): ParsedSocialLink {
  const trimmed = input.trim();
  if (!trimmed) return reject("invalid_url");
  if (trimmed.length > 2048) return reject("too_long");
  if (/\s/.test(trimmed)) return reject("spaces");
  if (EMAIL_PATTERN.test(trimmed.replace(/^@+/, ""))) return reject("email");

  const { rest, scheme } = stripSchemes(trimmed);
  if (!rest) return reject("invalid_url");

  // A leading @ is a handle marker, never part of an address
  const text = rest.replace(/^@+/, "");
  const url = splitUrl(text, scheme ?? "https");
  if (field === "website_url") return parseWebsiteLink(url);

  // Scheme-less text is a link only when it names a social host or has a
  // path; otherwise "first.last" is a username, and (for Substack alone) a
  // bare dotted name is a domain.
  const isLink =
    url !== null &&
    (scheme !== null ||
      isSocialHost(url.host) ||
      text.includes("/") ||
      field === "substack_url");
  if (scheme !== null && !url) return reject("invalid_url");
  if (isLink && url.hasUserInfo) return reject("invalid_url");

  return field === "substack_url"
    ? parseSubstackLink(text, isLink ? url : null)
    : parsePlatformLink(PLATFORMS[field], text, isLink ? url : null);
}

/**
 * What a stored link points at, for equality checks: scheme, "www.", case,
 * and trailing slashes never matter, and on social hosts neither does the
 * query string (share links differ only by tracking params). Two spellings of
 * the same link must compare equal, or canonicalizing a verified link would
 * read as the owner changing it.
 */
export function socialLinkIdentity(value: string): string {
  const { rest } = stripSchemes(value.trim());
  const url = splitUrl(rest.replace(/^@+/, ""), "https");
  if (!url) return rest.replace(/\/+$/, "").toLowerCase();
  const host = bareHost(url.host);
  const path = url.segments.join("/");
  const after = isSocialHost(url.host)
    ? ""
    : url.pathAndAfter.slice(url.pathAndAfter.search(/[?#]|$/));
  return `${host}${url.port ? `:${url.port}` : ""}/${path}${after}`.toLowerCase();
}

export function socialLinksEqual(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a?.trim() || !b?.trim()) return false;
  return socialLinkIdentity(a) === socialLinkIdentity(b);
}

/**
 * Error copy for a rejected link. `label` is the platform name as the form
 * shows it ("Instagram", "X / Twitter", "Website").
 */
export function getSocialLinkRejectMessage(
  field: SocialLinkField,
  reason: SocialLinkRejectReason,
  label: string,
): string {
  const isWebsite = field === "website_url";
  const ask = isWebsite
    ? "Enter a web address like https://example.com."
    : `Enter your ${label} username or paste your profile link.`;
  switch (reason) {
    case "email":
      return `That looks like an email address. ${ask}`;
    case "spaces":
      return isWebsite
        ? "A web address can't contain spaces. Enter one link per field."
        : `Usernames and links can't contain spaces. Enter one ${label} username or profile link.`;
    case "invalid_handle":
      return `That isn't a valid ${label} username. Check the spelling, or paste your profile link.`;
    case "wrong_site":
      return `That link isn't on ${label}. Paste your ${label} profile link, or add it under Website.`;
    case "not_profile":
      return `That link doesn't go to a profile. Paste the link to your ${label} profile.`;
    case "too_long":
      return `That link is too long to save. Paste a link under ${MAX_SOCIAL_LINK_LENGTH} characters.`;
    case "invalid_url":
      return `That doesn't look like a link. ${ask}`;
  }
}

export type SocialLinkInputResult =
  | { ok: true; url: string | null }
  | { ok: false; error: string };

/**
 * What a form should submit for one link input: null for a blank (cleared)
 * field, the canonical URL, or the error to show under the field. A value
 * that is exactly one of the `saved` links passes through untouched, so a
 * stored link from before this parser never blocks saving the rest of the
 * form.
 */
export function resolveSocialLinkInput(
  field: SocialLinkField,
  label: string,
  input: string,
  saved: readonly (string | null | undefined)[] = [],
): SocialLinkInputResult {
  const trimmed = input.trim();
  if (!trimmed) return { ok: true, url: null };
  if (saved.includes(trimmed)) return { ok: true, url: trimmed };
  const parsed = parseSocialLink(field, trimmed);
  return parsed.ok
    ? { ok: true, url: parsed.url }
    : { ok: false, error: getSocialLinkRejectMessage(field, parsed.reason, label) };
}
